"use client";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import Link from "next/link";
import type { CityDirectoryEntry } from "@/lib/data/applicators";

interface Props {
  state: string; // lowercase route segment
  initial: CityDirectoryEntry[];
}

export function CityDirectoryClient({ state, initial }: Props) {
  const [search, setSearch] = React.useState("");
  const debounced = useDebounce(search, 250);
  const { data, isFetching } = useQuery<{ items: CityDirectoryEntry[] }>({
    queryKey: ["cities", state, debounced],
    queryFn: async () => {
      const params = new URLSearchParams({
        state: state.toUpperCase(),
        pageSize: "500",
      });
      if (debounced) params.set("search", debounced);
      const res = await fetch(`/api/applicators/cities?${params.toString()}`, {
        next: { revalidate: 0 },
      });
      if (!res.ok) throw new Error("Failed to fetch cities");
      const json = await res.json();
      return { items: json.items as CityDirectoryEntry[] };
    },
    enabled: debounced.length > 0,
  });

  const list = debounced ? data?.items ?? [] : initial;

  return (
    <div>
      <div className="mb-6 max-w-md">
        <Input
          placeholder="Search cities in this state..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search cities"
        />
      </div>
      {debounced && isFetching && (
        <p className="text-sm text-muted-foreground mb-2">Searching…</p>
      )}
      {!list.length && debounced ? (
        <p className="text-sm text-muted-foreground">
          No cities match that query.
        </p>
      ) : (
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {list.map((c) => {
            const cityPart = c.slug.replace(/-([a-z]{2})$/i, "");
            return (
              <Link
                key={c.slug}
                href={`/applicators/${state}/${cityPart}`}
                className="group rounded-lg border bg-card/40 hover:bg-card transition-colors p-4 flex flex-col"
              >
                <span className="text-base font-medium tracking-tight group-hover:text-primary transition-colors">
                  {c.city}
                </span>
                <span className="text-xs text-muted-foreground mt-1">
                  {c.count} installer{c.count === 1 ? "" : "s"}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function useDebounce<T>(value: T, ms: number) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
